import React, { useCallback, useEffect, useMemo, useState } from "react"
import {
  Badge,
  Button,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import Select from "react-select"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import { useFormik } from "lib/formik"
import * as Yup from "yup"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import { formatPaymentMethodLabels } from "pages/AppSettings/paymentMethodConfig"
import {
  saAddBrand,
  saGetAllBrands,
  saGetBrandDetails,
  saGetBrandPaymentMethods,
  saToggleBrand,
  saUpdateBrand,
  saGetTenantFulfillmentIntegrations,
  saGetDeliveryProfileTemplates,
} from "store/actions/superAdminAction"
import { showAlert } from "store/reducers/alertReducer"

const EMPTY_FORM = {
  brandName: "",
  brandDesc: "",
  integration: "",
  deliveryProfileTemplateCode: "",
  allowedPaymentMethods: [],
  status: 1,
}

const SUGGESTED_TEMPLATE_BY_INTEGRATION = {
  salasa: "promise_window",
  syrve: "qsr_asap",
  foodics: "qsr_asap",
}

const buildSchema = t =>
  Yup.object({
    brandName: Yup.string()
      .trim()
      .min(2, t("Brand name must be at least 2 characters"))
      .required(t("Brand name is required")),
    brandDesc: Yup.string().trim().default(""),
    integration: Yup.string().nullable().default(null),
    allowedPaymentMethods: Yup.array().of(Yup.string()),
    status: Yup.number().oneOf([0, 1]),
  })

const TenantBrandsTab = ({ tenantId }) => {
  const { t, i18n } = useTranslation()
  const lang = i18n.language === "sa" ? "ar" : "en"
  const dispatch = useDispatch()

  const {
    brands,
    loading: brandsLoading,
    isLoading: brandActionLoading,
    paymentMethodOptions,
    fulfillmentIntegrations,
    fulfillmentIntegrationsLoading,
    deliveryProfileTemplates,
    deliveryProfileTemplatesLoading,
  } = useSelector(state => state.Brands)

  const [modalOpen, setModalOpen] = useState(false)
  const [editBrandId, setEditBrandId] = useState(null)

  const isEdit = Boolean(editBrandId)

  const integrationOptions = useMemo(() => {
    const none = { value: "", label: t("None (In-app only)") }
    const fromPlan = (fulfillmentIntegrations || []).map(item => {
      const titleStr =
        typeof item.title === "object"
          ? item.title?.[lang] || item.title?.en || item.title?.ar || item.code
          : item.title || item.code
      return {
        value: item.code,
        label: titleStr,
      }
    })
    return [none, ...fromPlan]
  }, [fulfillmentIntegrations, t, lang])

  const paymentMethodSelectOptions = useMemo(
    () =>
      (paymentMethodOptions || []).map(method => ({
        value: method,
        label: t(method, { defaultValue: method }),
      })),
    [paymentMethodOptions, t]
  )

  const loadBrands = useCallback(() => {
    if (tenantId) {
      dispatch(saGetAllBrands({ forTenantId: tenantId }))
    }
  }, [dispatch, tenantId])

  useEffect(() => {
    loadBrands()
    dispatch(saGetBrandPaymentMethods())
    if (tenantId) {
      dispatch(saGetTenantFulfillmentIntegrations({ tenantId }))
      dispatch(saGetDeliveryProfileTemplates({ tenantId }))
    }
  }, [dispatch, loadBrands, tenantId])

  const deliveryTemplateOptions = useMemo(
    () =>
      (deliveryProfileTemplates || []).map(template => ({
        value: template.code,
        label:
          lang === "ar" && template.nameAr ? template.nameAr : template.name,
      })),
    [deliveryProfileTemplates, lang]
  )

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: EMPTY_FORM,
    validationSchema: buildSchema(t),
    onSubmit: values => {
      const payload = {
        forTenantId: tenantId,
        brandName: values.brandName.trim(),
        brandDesc: (values.brandDesc || "").trim(),
        integration: values.integration || null,
        deliveryProfileTemplateCode: values.deliveryProfileTemplateCode || null,
        allowedPaymentMethods: values.allowedPaymentMethods || [],
        status: Number(values.status),
      }

      const action = isEdit
        ? dispatch(saUpdateBrand({ id: editBrandId, ...payload }))
        : dispatch(saAddBrand(payload))

      action.then(res => {
        const { statusCode, status, msg } = res?.payload || {}
        if ((statusCode === 200 || statusCode === 201) && status) {
          dispatch(showAlert({ text: msg || t("Saved successfully"), type: "success" }))
          closeModal()
          loadBrands()
        } else {
          dispatch(
            showAlert({
              text: msg ? t(msg, { defaultValue: msg }) : t("Something went wrong"),
              type: "error",
            })
          )
        }
      })
    },
  })

  const openAddModal = () => {
    setEditBrandId(null)
    formik.resetForm({ values: EMPTY_FORM })
    setModalOpen(true)
  }

  const openEditModal = brandId => {
    setEditBrandId(brandId)
    dispatch(saGetBrandDetails({ id: brandId, forTenantId: tenantId })).then(res => {
      const d = res?.payload?.data || {}
      formik.resetForm({
        values: {
          brandName: d.brandName || "",
          brandDesc: d.brandDesc || "",
          integration: d.integration || "",
          deliveryProfileTemplateCode: d.deliveryProfileTemplateCode || "",
          allowedPaymentMethods: Array.isArray(d.allowedPaymentMethods)
            ? d.allowedPaymentMethods
            : [],
          status: d.status === 0 ? 0 : 1,
        },
      })
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setEditBrandId(null)
    formik.resetForm({ values: EMPTY_FORM })
  }

  const handleToggle = brandId => {
    dispatch(saToggleBrand({ id: brandId, forTenantId: tenantId })).then(res => {
      if (res?.payload?.status) {
        loadBrands()
      }
    })
  }

  const brandList = Array.isArray(brands) ? brands : []

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0">{t("Brands")}</h5>
        <Button color="primary" size="sm" onClick={openAddModal}>
          <i className="bx bx-plus me-1" />
          {t("Add Brand")}
        </Button>
      </div>

      {brandsLoading ? (
        <div className="text-center py-4">
          <Spinner color="primary" />
        </div>
      ) : brandList.length === 0 ? (
        <p className="text-muted">{t("No brands for this tenant yet.")}</p>
      ) : (
        <Table responsive bordered striped size="sm">
          <thead>
            <tr>
              <th>{t("Brand Name")}</th>
              <th>{t("Integration")}</th>
              <th>{t("Delivery Profile")}</th>
              <th>{t("Payment Methods")}</th>
              <th>{t("Status")}</th>
              <th>{t("Actions")}</th>
            </tr>
          </thead>
          <tbody>
            {brandList.map(brand => (
              <tr key={brand._id}>
                <td>{brand.brandName}</td>
                <td>{brand.integration || <span className="text-muted">—</span>}</td>
                <td>{brand.deliveryProfileTemplateCode || <span className="text-muted">—</span>}</td>
                <td>
                  {formatPaymentMethodLabels(brand.allowedPaymentMethods, t) || (
                    <span className="text-muted">—</span>
                  )}
                </td>
                <td>
                  <Badge color={brand.status === 1 ? "success" : "secondary"}>
                    {brand.status === 1 ? t("Active") : t("Inactive")}
                  </Badge>
                </td>
                <td>
                  <TableActionsDropdown
                    items={[
                      {
                        key: "edit",
                        content: (
                          <>
                            <i className="bx bx-edit-alt"></i>
                            {t("Edit")}
                          </>
                        ),
                        onClick: () => openEditModal(brand._id),
                      },
                      {
                        key: "toggle",
                        content: (
                          <>
                            <i
                              className={`bx ${
                                brand.status === 1
                                  ? "bx-block text-warning"
                                  : "bx-check-circle text-success"
                              }`}
                            ></i>
                            {brand.status === 1 ? t("Deactivate") : t("Activate")}
                          </>
                        ),
                        onClick: () => handleToggle(brand._id),
                      },
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal isOpen={modalOpen} toggle={closeModal} size="lg">
        <ModalHeader toggle={closeModal}>
          {isEdit ? t("Edit Brand") : t("Add Brand")}
        </ModalHeader>
        <Form onSubmit={formik.handleSubmit}>
          <ModalBody>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>
                    {t("Brand Name")} <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="brandName"
                    value={formik.values.brandName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={isEdit}
                    invalid={formik.touched.brandName && !!formik.errors.brandName}
                  />
                  <FormFeedback>{formik.errors.brandName}</FormFeedback>
                  {isEdit && (
                    <small className="text-muted">{t("Brand name cannot be changed after creation.")}</small>
                  )}
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>{t("Integration")}</Label>
                  {fulfillmentIntegrationsLoading ? (
                    <div><Spinner size="sm" /> <small>{t("Loading plan integrations…")}</small></div>
                  ) : (
                    <Select
                      value={
                        integrationOptions.find(o => o.value === (formik.values.integration || "")) || integrationOptions[0]
                      }
                      onChange={opt => {
                        const integration = opt?.value || ""
                        formik.setFieldValue("integration", integration, true)
                        const suggested =
                          SUGGESTED_TEMPLATE_BY_INTEGRATION[integration] ||
                          "hybrid_ecommerce"
                        if (
                          !formik.values.deliveryProfileTemplateCode ||
                          !isEdit
                        ) {
                          formik.setFieldValue(
                            "deliveryProfileTemplateCode",
                            suggested,
                            true
                          )
                        }
                      }}
                      options={integrationOptions}
                      placeholder={t("None (In-app only)")}
                      classNamePrefix="select2-selection"
                    />
                  )}
                  <small className="text-muted">
                    {t("Only integrations enabled on the tenant's plan are shown.")}
                  </small>
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup>
                  <Label>{t("Delivery Profile")}</Label>
                  {deliveryProfileTemplatesLoading ? (
                    <div>
                      <Spinner size="sm" />{" "}
                      <small>{t("Loading delivery profiles…")}</small>
                    </div>
                  ) : (
                    <Select
                      value={
                        deliveryTemplateOptions.find(
                          o =>
                            o.value ===
                            (formik.values.deliveryProfileTemplateCode || "")
                        ) || null
                      }
                      onChange={opt =>
                        formik.setFieldValue(
                          "deliveryProfileTemplateCode",
                          opt?.value || "",
                          true
                        )
                      }
                      options={deliveryTemplateOptions}
                      placeholder={t("Select delivery profile")}
                      classNamePrefix="select2-selection"
                    />
                  )}
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label>{t("Description")}</Label>
                  <Input
                    type="textarea"
                    name="brandDesc"
                    rows={3}
                    value={formik.values.brandDesc}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                </FormGroup>
              </Col>

              <Col md={12}>
                <FormGroup>
                  <Label>{t("Allowed Payment Methods")}</Label>
                  <Select
                    isMulti
                    value={paymentMethodSelectOptions.filter(o =>
                      (formik.values.allowedPaymentMethods || []).includes(o.value)
                    )}
                    onChange={opts =>
                      formik.setFieldValue(
                        "allowedPaymentMethods",
                        (opts || []).map(o => o.value),
                        true
                      )
                    }
                    options={paymentMethodSelectOptions}
                    placeholder={t("Select payment methods")}
                    classNamePrefix="select2-selection"
                    styles={{
                      multiValue: p => ({ ...p, backgroundColor: "#556ee6" }),
                      multiValueLabel: p => ({ ...p, color: "#fff" }),
                      multiValueRemove: p => ({
                        ...p,
                        color: "#fff",
                        ":hover": { backgroundColor: "#4458b8", color: "#fff" },
                      }),
                    }}
                  />
                </FormGroup>
              </Col>

              <Col md={6}>
                <FormGroup check className="mt-2">
                  <Input
                    type="checkbox"
                    id="brand-status"
                    checked={formik.values.status === 1}
                    onChange={e =>
                      formik.setFieldValue("status", e.target.checked ? 1 : 0)
                    }
                  />
                  <Label check for="brand-status" className="ms-2">
                    {t("Active")}
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button type="submit" color="primary" disabled={brandActionLoading}>
              {brandActionLoading ? <Spinner size="sm" /> : isEdit ? t("Save Changes") : t("Add Brand")}
            </Button>
            <Button type="button" color="secondary" outline onClick={closeModal}>
              {t("Cancel")}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>
    </div>
  )
}

export default TenantBrandsTab
